import { useEffect, useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, FolderKanban, DollarSign, Receipt, Wallet } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { format } from 'date-fns';

const COLORS = ['#F97316', '#FDBA74', '#10B981', '#64748B', '#EF4444'];

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isPositive, testId }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
          <Icon className="w-6 h-6 text-orange-600" strokeWidth={1.5} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{trend}</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
        <p className="text-3xl font-heading font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [revenueVsExpenses, setRevenueVsExpenses] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [contributionSummary, setContributionSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [metricsRes, revenueRes, breakdownRes, transactionsRes, contribRes] = await Promise.all([
        api.get('/dashboard/metrics'),
        api.get('/dashboard/revenue-vs-expenses'),
        api.get('/dashboard/expense-breakdown'),
        api.get('/dashboard/recent-transactions'),
        api.get('/contributions/summary'),
      ]);
      setMetrics(metricsRes.data);
      setRevenueVsExpenses(revenueRes.data);
      setExpenseBreakdown(breakdownRes.data);
      setRecentTransactions(transactionsRes.data);
      setContributionSummary(contribRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Dashboard" subtitle="Overview of your projects and expenses" />

      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Revenue (This Month)"
            value={formatCurrency(metrics.total_revenue_month)}
            subtitle={`All time: ${formatCurrency(metrics.total_revenue_all)}`}
            icon={DollarSign}
            testId="metric-revenue-month"
          />
          <MetricCard
            title="Total Expenses (This Month)"
            value={formatCurrency(metrics.total_expenses_month)}
            subtitle={`All time: ${formatCurrency(metrics.total_expenses_all)}`}
            icon={Receipt}
            testId="metric-expenses-month"
          />
          <MetricCard
            title="Net Profit (This Month)"
            value={formatCurrency(metrics.net_profit_month)}
            subtitle={`All time: ${formatCurrency(metrics.net_profit_all)}`}
            icon={TrendingUp}
            isPositive={metrics.net_profit_month >= 0}
            testId="metric-profit-month"
          />
          <MetricCard
            title="Active Projects"
            value={metrics.active_projects}
            subtitle={`Pending: ${formatCurrency(metrics.pending_payment)}`}
            icon={FolderKanban}
            testId="metric-active-projects"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-8 bg-white rounded-xl border border-slate-100 p-6 shadow-sm" data-testid="revenue-vs-expenses-chart">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-6">Revenue vs Expenses</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueVsExpenses}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" style={{ fontSize: '12px' }} />
                <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#F97316" name="Revenue" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expenses" fill="#64748B" name="Expenses" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-4 bg-white rounded-xl border border-slate-100 p-6 shadow-sm" data-testid="expense-breakdown-chart">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-6">Expense Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Owner Contributions */}
        {Object.keys(contributionSummary).length > 0 && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                Owner Funding
              </h3>
              <a href="/contributions" className="text-xs text-orange-500 hover:underline font-medium">View all →</a>
            </div>
            <div className="flex flex-wrap gap-4">
              {Object.entries(contributionSummary).map(([owner, amt]) => (
                <div key={owner} className="flex-1 min-w-36 bg-slate-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{owner}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ₹{amt.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">total contributed</p>
                </div>
              ))}
              <div className="flex-1 min-w-36 bg-orange-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1">Combined</p>
                <p className="text-2xl font-bold text-orange-700">
                  ₹{Object.values(contributionSummary).reduce((s, v) => s + v, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-orange-400 mt-0.5">total funded</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="recent-transactions-table">
          <div className="px-6 py-4 border-b border-slate-50">
            <h3 className="text-lg font-heading font-semibold text-slate-900">Recent Transactions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Description</th>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                      {format(new Date(transaction.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">{transaction.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                          transaction.type === 'revenue'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-red-50 text-red-700 border border-red-100'
                        }`}
                      >
                        {transaction.type === 'revenue' ? 'Revenue' : 'Expense'}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold text-right ${transaction.type === 'revenue' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {transaction.type === 'revenue' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
